import { toast } from "sonner";

/**
 * Optimistically remove an item from local state, show a sonner toast with
 * an "Undo" action, and only call the backend DELETE after the toast expires.
 *
 * If the user clicks Undo within `delayMs` the actual API call is cancelled
 * and the item is restored to local state. If the API call ultimately fails,
 * the item is restored and an error toast is shown.
 *
 * @param {object}   args
 * @param {string}   args.label          Human label (e.g. "Task")
 * @param {object}   args.item           Full item object (must include id)
 * @param {(id:string)=>Promise} args.apiDelete  API delete call
 * @param {()=>void} args.onLocalRemove  Remove item from local state
 * @param {()=>void} args.onLocalRestore Restore item to local state on undo
 * @param {()=>void} [args.onCommit]     Called when the delete is finalized
 * @param {number}   [args.delayMs]      Undo window (default 5000)
 */
export function undoableDelete({
    label = "Item",
    item,
    apiDelete,
    onLocalRemove,
    onLocalRestore,
    onCommit,
    delayMs = 5000,
}) {
    onLocalRemove();
    let undone = false;

    const timer = setTimeout(async () => {
        if (undone) return;
        try {
            await apiDelete(item.id);
            onCommit?.();
        } catch (e) {
            onLocalRestore();
            toast.error(`Couldn't delete ${label.toLowerCase()}`);
        }
    }, delayMs);

    toast(`${label} deleted`, {
        duration: delayMs,
        action: {
            label: "Undo",
            onClick: () => {
                if (undone) return;
                undone = true;
                clearTimeout(timer);
                onLocalRestore();
            },
        },
    });
}
